import React, { useState, useEffect, useRef } from 'react';
import Editor from '@monaco-editor/react';
import { motion } from 'framer-motion';
import { apiGet } from '../utils/apiUtils';
import {
    FiCopy,
    FiCheck,
    FiCode,
    FiLock,
    FiUnlock,
    FiAlertCircle,
    FiChevronDown
} from 'react-icons/fi';

/**
 * @file SolutionPanel.jsx
 * @description Renders the multi-language solution viewer for practice problems.
 *              Handles language switching, copy-to-clipboard, and skeleton loading states.
 * @module components/SolutionPanel
 */

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component SolutionPanel
 * @description The main solution code viewer.
 * 
 * @param {Object} props
 * @param {string|number} props.questionId - The ID of the practice problem.
 * @param {boolean} props.isAuthenticated - User auth state to gate access.
 * @param {Function} props.navigate - Router navigation function.
 * @returns {JSX.Element} The rendered Solution Panel view.
 */
const SolutionPanel = ({ questionId, isAuthenticated, navigate }) => {
    const [activeLanguage, setActiveLanguage] = useState('cpp');
    const [copied, setCopied] = useState(false);
    const [isMobileView, setIsMobileView] = useState(false);
    const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
    const [solutions, setSolutions] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const editorRef = useRef(null);

    const languageConfig = {
        cpp: { id: 52, name: 'C++', monacoLang: 'cpp', logo: 'cpp.png' },
        java: { id: 62, name: 'Java', monacoLang: 'java', logo: 'java.png' },
        javascript: { id: 63, name: 'JavaScript', monacoLang: 'javascript', logo: 'javascript.png' },
        python: { id: 71, name: 'Python', monacoLang: 'python', logo: 'python.png' }
    };

    /* ══════════════════════════════════════════════════════════════
     * EFFECTS & HANDLERS
     * ══════════════════════════════════════════════════════════════ */

    useEffect(() => {
        const checkMobile = () => {
            setIsMobileView(window.innerWidth < 1024);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (isAuthenticated && questionId) {
            fetchSolutions();
        }
    }, [questionId, isAuthenticated]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.language-selector')) {
                setShowLanguageDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchSolutions = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await apiGet(`/solutions/${questionId}`);

            if (!response.ok) {
                throw new Error('Failed to fetch solutions');
            }

            const data = await response.json();
            setSolutions(data.solutions);

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const copySolution = async () => {
        const currentSolution = solutions?.[activeLanguage];
        if (!currentSolution?.solution) return;

        try {
            await navigator.clipboard.writeText(currentSolution.solution);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
        }
    };

    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;

        monaco.editor.defineTheme('solution-dark', {
            base: 'vs-dark',
            inherit: true,
            rules: [
                { token: 'comment', foreground: '6A9955', fontStyle: 'italic' },
                { token: 'keyword', foreground: '569CD6' },
                { token: 'string', foreground: 'CE9178' },
                { token: 'number', foreground: 'B5CEA8' },
            ],
            colors: {
                'editor.background': '#0d1117',
                'editor.foreground': '#e6edf3',
                'editorLineNumber.foreground': '#7d8590',
                'editor.selectionBackground': '#264f78',
                'editor.lineHighlightBackground': '#161b22'
            }
        });

        monaco.editor.setTheme('solution-dark');
    };

    if (!isAuthenticated) {
        return (
            <div className="w-full h-full flex items-center justify-center bg-accent">
                <div className="text-center mx-4 p-8" style={{ maxWidth: '400px' }}>
                    <div className="mb-4">
                        <h3 className="text-xl font-semibold text-foreground mb-2 flex items-center justify-center space-x-2 whitespace-nowrap">
                            <p>Solutions are Locked</p>
                            <FiLock />
                        </h3>
                        <p className="hidden md:block text-muted-foreground text-sm leading-relaxed whitespace-nowrap">
                            Sign in to view solution code for this problem...
                        </p>
                    </div>

                    <button
                        onClick={() => navigate('/signin')}
                        className="w-full px-6 py-3 text-emerald-600 rounded font-medium transition-all duration-200 flex items-center justify-center space-x-2 whitespace-nowrap"
                    >
                        <FiUnlock className="text-sm" />
                        <span>Sign In</span>
                    </button>
                </div>
            </div>
        );
    }

    if (loading) {
        return <SolutionSkeleton />;
    }

    if (error) {
        return (
            <div className="h-full flex items-center justify-center bg-accent">
                <div className="text-center max-w-md mx-4">
                    <FiAlertCircle className="text-4xl text-red-500 mx-auto mb-3" />
                    <p className="text-muted-foreground mb-4">Failed to load solutions</p>
                    <button
                        onClick={fetchSolutions}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded transition-colors duration-200"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    const hasSolutions = solutions && Object.values(solutions).some(s => s !== null);

    if (!hasSolutions) {
        return (
            <div className="h-full flex items-center justify-center bg-accent">
                <div className="text-center max-w-md mx-4">
                    <FiCode className="text-5xl text-gray-400 mx-auto mb-4 opacity-50" />
                    <h3 className="text-lg font-medium text-muted-foreground mb-2">No Solutions Available Yet</h3>
                    <p className="text-muted-foreground text-sm">
                        Solutions are being prepared for this problem. Check back soon!
                    </p>
                </div>
            </div>
        );
    }

    const currentSolution = solutions?.[activeLanguage];

    return (
        <div className="flex flex-col bg-accent overflow-hidden" style={{ height: 'calc(102vh - 200px)' }}>
            <div className="bg-accent border-b border-gray-300 py-1 flex items-center justify-between flex-shrink-0">
                <div className="hidden lg:flex space-x-2 overflow-x-auto">
                    {Object.entries(languageConfig).map(([key, config]) => {
                        const hasSolution = solutions?.[key] !== null;
                        const isActive = activeLanguage === key;

                        return (
                            <button
                                key={key}
                                onClick={() => setActiveLanguage(key)}
                                className={`group relative p-2 transition-all duration-300`}
                                title={config.name}
                            >
                                <img
                                    src={`/languages/${config.logo}`}
                                    alt={config.name}
                                    className={`w-8 h-8 object-contain transition-all duration-300 ${isActive
                                        ? 'grayscale-0'
                                        : 'grayscale opacity-100'
                                        }`}
                                />
                            </button>
                        );
                    })}
                </div>

                <div className="lg:hidden relative language-selector">
                    <button
                        onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                        className="px-3 py-1 bg-accent border border-gray-300 rounded text-sm font-medium transition-all duration-200 flex items-center space-x-3 hover:bg-gray-50 text-foreground shadow-sm"
                    >
                        <img 
                            src={`/languages/${languageConfig[activeLanguage]?.logo}`} 
                            alt={activeLanguage} 
                            className="w-5 h-5 object-contain"
                        />
                        <span className="font-semibold">{languageConfig[activeLanguage]?.name}</span>
                        <FiChevronDown className={`text-sm transform transition-transform duration-300 ${showLanguageDropdown ? 'rotate-180' : ''}`} />
                    </button>

                    {showLanguageDropdown && (
                        <div className="absolute left-0 top-full mt-2.5 bg-accent border border-gray-300 rounded shadow-lg z-50 min-w-[140px] overflow-hidden">
                            {Object.entries(languageConfig).map(([key, config]) => {
                                const hasSolution = solutions?.[key] !== null;
                                const isActive = activeLanguage === key;

                                return (
                                    <button
                                        key={key}
                                        onClick={() => {
                                            setActiveLanguage(key);
                                            setShowLanguageDropdown(false);
                                        }}
                                        className={`w-full text-left px-3 py-2 text-sm transition-all duration-200 flex items-center justify-between ${isActive
                                            ? 'bg-emerald-600 text-white'
                                            : 'text-foreground hover:bg-gray-50'
                                            }`}
                                    >
                                        <div className="flex items-center space-x-3">
                                            <img 
                                                src={`/languages/${config.logo}`} 
                                                alt={config.name} 
                                                className={`w-5 h-5 object-contain ${isActive ? 'grayscale-0' : 'grayscale opacity-70'}`}
                                            />
                                            <span className="font-medium">{config.name}</span>
                                        </div>
                                        {isActive && (
                                            <FiCheck className="text-xs" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className='pb-1'>
                    {currentSolution && (
                        <button
                            onClick={copySolution}
                            className="flex items-center space-x-2 px-3 py-1.5 text-muted-foreground hover:text-foreground transition-all duration-200 text-sm"
                        >
                            {copied ? (
                                <>
                                    <FiCheck className="text-emerald-600" />
                                    <span className="text-emerald-600">Copied!</span>
                                </>
                            ) : (
                                <>
                                    <FiCopy />
                                    <span>Copy Code</span>
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>

            <div className="flex-1 overflow-hidden pt-2" style={{ minHeight: 0 }}>
                {currentSolution ? (
                    <div className="w-full h-full">
                        <div style={{ width: '100%', height: '100%' }} className='font-mono'>
                            <Editor
                                height="100%"
                                width="100%"
                                defaultLanguage={languageConfig[activeLanguage].monacoLang}
                                defaultValue={currentSolution.solution}
                                theme="vs-dark"
                                options={{
                                    readOnly: true,
                                    fontSize: 14,
                                    fontLigatures: true,
                                    minimap: { enabled: false },
                                    scrollBeyondLastLine: false,
                                    wordWrap: 'on',
                                    automaticLayout: true,
                                    lineNumbers: 'on',
                                    renderLineHighlight: 'all',
                                    contextmenu: false,
                                    domReadOnly: true,
                                    cursorStyle: 'block',
                                    lineDecorationsWidth: 0,
                                    lineNumbersMinChars: 3
                                }}
                                loading={
                                    <div className="flex items-center justify-center h-full bg-zinc-900">
                                        <div className="text-center">
                                            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                                            <p className="text-gray-400 text-sm">Loading solution...</p>
                                        </div>
                                    </div>
                                }
                            />
                        </div>
                    </div>
                ) : (
                    <div className="h-full flex items-center justify-center bg-accent">
                        <div className="text-center max-w-md mx-4">
                            <h3 className="text-lg font-medium text-emerald-600 mb-2">
                                {languageConfig[activeLanguage].name} Solution Coming Soon...
                            </h3>
                            <br></br>
                            <p className="text-muted-foreground text-sm">
                                This solution hasn't been added yet. <br></br>Try viewing other language solutions.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════
 * SKELETON LOADER
 * ══════════════════════════════════════════════════════════════ */

const SolutionSkeleton = () => {
    return (
        <div className="flex flex-col bg-accent overflow-hidden" style={{ height: 'calc(102vh - 200px)' }}>
            <div className="bg-gray-50 border-b border-gray-300 py-1 flex items-center justify-between flex-shrink-0 animate-pulse">
                <div className="hidden lg:flex space-x-2 pb-2">
                    <div className="h-8 w-16 bg-gray-100 rounded"></div>
                    <div className="h-8 w-20 bg-gray-100 rounded"></div>
                    <div className="h-8 w-24 bg-gray-100 rounded"></div>
                    <div className="h-8 w-16 bg-gray-100 rounded"></div>
                </div>
                <div className="lg:hidden">
                    <div className="h-8 w-32 bg-gray-100 rounded"></div>
                </div>
                <div className="pb-1">
                    <div className="h-8 w-28 bg-gray-100 rounded"></div>
                </div>
            </div>

            <div className="flex-1 overflow-hidden pt-2 p-4 animate-pulse" style={{ minHeight: 0 }}>
                <div className="space-y-3">
                    <div className="h-4 w-3/4 bg-gray-100 rounded"></div>
                    <div className="h-4 w-full bg-gray-100 rounded"></div>
                    <div className="h-4 w-5/6 bg-gray-100 rounded"></div>
                    <div className="h-4 w-full bg-gray-100 rounded"></div>
                    <div className="h-4 w-2/3 bg-gray-100 rounded"></div>
                    <div className="h-4 w-full bg-gray-100 rounded"></div>
                    <div className="h-4 w-4/5 bg-gray-100 rounded"></div>
                    <div className="h-4 w-full bg-gray-100 rounded"></div>
                    <div className="h-4 w-3/4 bg-gray-100 rounded"></div>
                    <div className="h-4 w-full bg-gray-100 rounded"></div>
                    <div className="h-4 w-5/6 bg-gray-100 rounded"></div>
                    <div className="h-4 w-2/3 bg-gray-100 rounded"></div>
                </div>
            </div>
        </div>
    );
};

export default SolutionPanel;